import React, { useEffect, useState, useCallback } from "react";
import { useROIData } from "@/hooks/post-analysis/useROIData";
import { fetchFilterOptions } from "@/services/api";
import MetricCardGroup from "../ui/MetricCardGroup";
import BaseChart from "../ui/BaseChart";
import ROISkeleton from "../ui/ROISkeleton";
import { formatValue } from "@/lib/post_analysis_utils/formatValue";
import { exportToCSV } from "@/lib/post_analysis_utils/exportToCSV";
import TableSkeleton from "../ui/TableSkeleton";
import UnifiedTable from "../tables/UnifiedTable";
import { ROI_COLUMNS } from "../tables/table_columns";
import { usePostAnalysisFilters } from "@/hooks/post-analysis/usePostAnalysisFilters";
import PostAnalysisHeaderWithFilters from "../ui/PostAnalysisHeaderWithFilters";

// Static configuration for metric cards

const INPUT_METRICS = [
    {
        title:"Traditional Budget",
        value : (summary)=> formatValue(280850,{type: "currency"})
    },
]
const MODEL_METRICS = [
    // {
    //     title: "Campaign Spend",
    //     value: (summary) => formatValue(56528, { type: "currency" })
    // },
    {
        title:"Optimized Budget",
        value : (summary)=> formatValue(232441,{type: "currency"})
    },
    
    
    // {
    //     title: "Number Of Campaigns",
    //     value: (summary) => formatValue(summary?.num_campaigns, { type: "number", decimals: 0 })
    // }
    // {
    //     title: "Number Of POCs Added",
    //     value: (summary) => formatValue(2180, { type: "number", decimals: 1 })
    // }
];
const OUTPUT_METRICS = [
    {
        title:"Budget Saved",
        value: (summary) => formatValue(48409, { type: "currency" })
    },
    // {
    //     title:"Savings Percentage",
    //     value: (summary) => formatValue(48409 / 280850, { type: "percentage", decimals: 1 })
    // },
    {
        title:"Count of POCs Added",
        value: (summary) => formatValue(194, { type: "number" ,decimals:0})
    },
    {
        title: "Revenue Uplift",
        value: (summary) => formatValue(summary?.uplift_revenue, { type: "currency" })
    },
    
]

export default function DashboardROI({ filters, setFilters }) {
    const [filterOptions, setFilterOptions] = useState({ brands: [], regions: [], campaign_names: [] });
    const [showFilters, setShowFilters] = useState(false);
    
    // Pass campaign name as-is (no title-casing)
    const { data, summary, upliftByRegion, upliftByBrand, isLoading, isTableLoading, error, page, setPage, pageSize, setPageSize, totalPages, totalRows, getAllROIData } = useROIData(filters);

    // Create stable string representation of poc_id array to prevent infinite loops
    const pocIdKey = React.useMemo(() => {
        const pocId = Array.isArray(filters.poc_id) ? filters.poc_id : (filters.poc_id ? [filters.poc_id] : []);
        return JSON.stringify(pocId.sort());
    }, [filters.poc_id]);

    // Create stable string representation of campaign array to prevent infinite loops
    const campaignKey = React.useMemo(() => {
        const campaign = Array.isArray(filters.campaign) ? filters.campaign : (filters.campaign ? [filters.campaign] : []);
        return JSON.stringify(campaign.sort());
    }, [filters.campaign]);

    // Create stable string representation of poc_name array to prevent infinite loops
    const pocNameKey = React.useMemo(() => {
        const pocName = Array.isArray(filters.poc_name) ? filters.poc_name : (filters.poc_name ? [filters.poc_name] : []);
        return JSON.stringify(pocName.sort());
    }, [filters.poc_name]);

    // Track previous filter values to prevent unnecessary re-fetches
    const prevFiltersRef = React.useRef({});

    // Shared local filter options seeded from current filterOptions
    // We manage the local handleLocalFiltersChange callback explicitly below.
    const { localFilterOptions, setLocalFilterOptions } =
      usePostAnalysisFilters(filterOptions);

    // Fetch filter options when filters change
    useEffect(() => {
        // Create a key to compare filter values
        const filterKey = JSON.stringify({
            region: filters.region,
            brand: filters.brand,
            campaign: campaignKey,
            start_date: filters.start_date,
            end_date: filters.end_date,
            poc_id: pocIdKey,
            poc_name: pocNameKey,
            key_account: filters.key_account
        });

        // Only fetch if filters actually changed (or on initial mount)
        if (prevFiltersRef.current.filterKey === filterKey && prevFiltersRef.current.filterKey !== undefined) {
            return;
        }

        prevFiltersRef.current.filterKey = filterKey;

        async function loadOptions() {
            // console.log("Fetching filter options...");
            const pocIdArray = Array.isArray(filters.poc_id) ? filters.poc_id : (filters.poc_id ? [filters.poc_id] : []);
            const campaignArray = Array.isArray(filters.campaign) ? filters.campaign : (filters.campaign ? [filters.campaign] : []);
            const pocNameArray = Array.isArray(filters.poc_name) ? filters.poc_name : (filters.poc_name ? [filters.poc_name] : []);
            const opts = await fetchFilterOptions(
                filters.region, 
                filters.brand, 
                campaignArray.length > 0 ? campaignArray : undefined, 
                filters.start_date, 
                filters.end_date,
                pocIdArray.length > 0 ? pocIdArray : undefined,
                pocNameArray.length > 0 ? pocNameArray : undefined,
                filters.key_account
            );
            // console.log("Filter options result:", opts);
            setFilterOptions(opts);
        }
        loadOptions();
    }, [filters.region, filters.brand, campaignKey, filters.start_date, filters.end_date, pocIdKey, pocNameKey, filters.key_account]);

    // Callback to fetch filter options based on local filters
    const handleLocalFiltersChange = useCallback(async (localFilters) => {
        try {
            const campaignArray = Array.isArray(localFilters.campaign) ? localFilters.campaign : (localFilters.campaign ? [localFilters.campaign] : []);
            const pocIdArray = Array.isArray(localFilters.poc_id) ? localFilters.poc_id : (localFilters.poc_id ? [localFilters.poc_id] : []);
            const pocNameArray = Array.isArray(localFilters.poc_name) ? localFilters.poc_name : (localFilters.poc_name ? [localFilters.poc_name] : []);
            
            const options = await fetchFilterOptions(
                localFilters.region || undefined,
                localFilters.brand || undefined,
                campaignArray.length > 0 ? campaignArray : undefined,
                localFilters.start_date || undefined,
                localFilters.end_date || undefined,
                pocIdArray.length > 0 ? pocIdArray : undefined,
                pocNameArray.length > 0 ? pocNameArray : undefined,
                localFilters.key_account || undefined
            );
            setLocalFilterOptions(options || {});
        } catch (err) {
            console.error('Error fetching local filter options:', err);
        }
    }, []);

    // Reset page to 1 when filters change
    useEffect(() => {
        setPage(1);
    }, [filters]);

    // Prepare metric cards with current summary data
    const metricCardsInput = INPUT_METRICS.map(card => ({
        title: card.title,
        value: card.value(summary)
    }));

    const mertricCardsModel = MODEL_METRICS.map(card => ({
        title: card.title,
        value: card.value(summary)
    }));

    
    const mertricCardsOutput = OUTPUT_METRICS.map(card => ({
        title: card.title,
        value: card.value(summary)
    }));
    // Loading and error states
    if (isLoading) {
        return <ROISkeleton />;
    }
    if (error) {
        return <div className="p-6 text-red-500 text-center">Error: {error.message}</div>;
    }

    // Add unique keys to data rows
    const tableData = data.map((row, index) => ({
        ...row,
        uniqueId: `${row.campaign_id}_${row.id}_${index}`
    }));

    // Pagination controls for ROI table
    // const renderTablePagination = () => (
    //     <div className="flex items-center justify-between mt-2">
    //         <div>
    //             Page {page} of {totalPages}
    //         </div>
    //         <div className="flex gap-2">
    //             <button
    //                 className="px-2 py-1 rounded border text-sm disabled:opacity-50"
    //                 onClick={() => setPage(page - 1)}
    //                 disabled={page === 1}
    //             >
    //                 Previous
    //             </button>
    //             <button
    //                 className="px-2 py-1 rounded border text-sm disabled:opacity-50"
    //                 onClick={() => setPage(page + 1)}
    //                 disabled={page === totalPages}
    //             >
    //                 Next
    //             </button>
    //         </div>
    //         <div>
    //             Rows per page:
    //             <select
    //                 className="ml-2 border rounded px-1 py-0.5 text-sm"
    //                 value={pageSize}
    //                 onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
    //             >
    //                 {[10, 20, 50, 100].map(size => (
    //                     <option key={size} value={size}>{size}</option>
    //                 ))}
    //             </select>
    //         </div>
    //     </div>
    // );

    // Export all ROI table data to CSV
    const handleExportAllCSV = async () => {
        try {
            // Fetch all rows from hook
            const response = await getAllROIData();
            const allRows = (response.data || []).map((row, index) => ({
                ...row,
                uniqueId: `${row.campaign_id}_${row.id}_${index}`
            }));
            exportToCSV(allRows, 'roi_table.csv');
        } catch (err) {
            alert('Failed to export all data. Please try again.');
        }
    };

    return (
        <div className="p-6">
            <PostAnalysisHeaderWithFilters
              title="ROI Analysis"
              filters={filters}
              setFilters={setFilters}
              baseFilterOptions={filterOptions}
              localFilterOptions={localFilterOptions}
              handleLocalFiltersChange={handleLocalFiltersChange}
            />
            <MetricCardGroup metrics={metricCardsInput} columns={4} title="Planned Budget"  />
            <MetricCardGroup metrics={mertricCardsModel} columns={4} title="Budget Spent after Optimization through model"/>
            <MetricCardGroup metrics={mertricCardsOutput} columns={4} title="Final Output After Optimization"/>
            <div className="flex flex-col md:flex-row gap-8 mt-8">
                <div className="flex-1 h-full">
                    <h2 className="text-xl font-semibold mb-2">Business Value (MACO$) Uplift by Region</h2>
                    <BaseChart
                        data={upliftByRegion}
                        xKey="region"
                        yKey="uplift_revenue"
                        type="bar"
                        color="#059669"
                        height={450}
                        xLabel="Region"
                        yLabel="Business Value (MACO$)"
                        yAxisFormatter={v => `$${v.toLocaleString()}`}
                        tooltipFormatter={v => `$${v.toLocaleString()}`}
                        legendLabel="Business Value (MACO$)"
                    />
                </div>
                <div className="flex-1 h-full">
                    <h2 className="text-xl font-semibold mb-2">Business Value (MACO$) Uplift by Brand</h2>
                    <BaseChart
                        data={upliftByBrand}
                        xKey="brand"
                        yKey="uplift_revenue"
                        type="bar"
                        color="#2c7392"
                        height={450}
                        xLabel="Brand"
                        yLabel="Business Value (MACO$)"
                        yAxisFormatter={v => `$${v.toLocaleString()}`}
                        tooltipFormatter={v => `$${v.toLocaleString()}`}
                        legendLabel="Business Value (MACO$)"
                    />
                </div>
            </div>
            {/* ROI Table Section */}

            <UnifiedTable
              columns={ROI_COLUMNS}
              data={tableData}
              isLoading={isTableLoading}
              page={page}
              setPage={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              totalPages={totalPages}
              title="ROI Table"
              onExportCSV={handleExportAllCSV}
            />
        </div>
    );
} 